import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { Globe, ArrowLeft, Mail, Lock, User, Phone } from "lucide-react";
import { Link } from "react-router-dom";
import { z } from "zod";

const authSchema = z.object({
  email: z.string().trim().email({ message: "البريد الإلكتروني غير صالح" }).max(255),
  password: z.string().min(6, { message: "كلمة المرور يجب أن تكون 6 أحرف على الأقل" }).max(72),
});

const registerSchema = authSchema.extend({
  fullName: z.string().trim().min(2, { message: "الاسم يجب أن يكون حرفين على الأقل" }).max(100),
  phone: z.string().optional(),
});

const Auth = () => {
  const { t, i18n } = useTranslation();
  const isRTL = i18n.language === "ar";
  const navigate = useNavigate();
  const [isLogin, setIsLogin] = useState(true);
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    email: "",
    password: "",
    fullName: "",
    phone: "",
  });

  useEffect(() => {
    const { data: { subscription } } = supabase.auth.onAuthStateChange((event, session) => {
      if (session?.user) {
        navigate("/");
      }
    });

    supabase.auth.getSession().then(({ data: { session } }) => {
      if (session?.user) {
        navigate("/");
      }
    });

    return () => subscription.unsubscribe();
  }, [navigate]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);

    try {
      if (isLogin) {
        const validation = authSchema.safeParse(formData);
        if (!validation.success) {
          toast.error(validation.error.errors[0].message);
          setLoading(false);
          return;
        }

        const { error } = await supabase.auth.signInWithPassword({
          email: formData.email,
          password: formData.password,
        });

        if (error) {
          if (error.message.includes("Invalid login credentials")) {
            toast.error(isRTL ? "بيانات تسجيل الدخول غير صحيحة" : "Invalid login credentials");
          } else {
            toast.error(error.message);
          }
        } else {
          toast.success(isRTL ? "تم تسجيل الدخول بنجاح" : "Logged in successfully");
        }
      } else {
        const validation = registerSchema.safeParse(formData);
        if (!validation.success) {
          toast.error(validation.error.errors[0].message);
          setLoading(false);
          return;
        }

        const redirectUrl = `${window.location.origin}/`;

        const { data: authData, error: authError } = await supabase.auth.signUp({
          email: formData.email,
          password: formData.password,
          options: {
            emailRedirectTo: redirectUrl,
            data: {
              full_name: formData.fullName,
            },
          },
        });

        if (authError) {
          if (authError.message.includes("already registered")) {
            toast.error(isRTL ? "هذا البريد الإلكتروني مسجل مسبقاً" : "This email is already registered");
          } else {
            toast.error(authError.message);
          }
        } else if (authData.user) {
          // Create visitor profile
          const { error: visitorError } = await supabase.from("visitors").insert({
            user_id: authData.user.id,
            full_name: formData.fullName,
            email: formData.email,
            phone: formData.phone || null,
          });

          if (visitorError) {
            console.error("Error creating visitor profile:", visitorError);
          }

          // Send registration notification email to admin
          try {
            await supabase.functions.invoke("send-registration-email", {
              body: {
                fullName: formData.fullName,
                email: formData.email,
                phone: formData.phone || null,
              },
            });
            console.log("Registration email sent to admin");
          } catch (emailError) {
            console.error("Error sending registration email:", emailError);
          }

          toast.success(isRTL ? "تم إنشاء الحساب بنجاح" : "Account created successfully");
        }
      }
    } catch (error) {
      console.error("Auth error:", error);
      toast.error(isRTL ? "حدث خطأ غير متوقع" : "An unexpected error occurred");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className={`min-h-screen bg-background ${isRTL ? "rtl" : "ltr"}`}>
      <nav className="border-b bg-white w-full">
        <div className="max-w-[2000px] mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center justify-between">
            <Link
              to="/"
              className="flex items-center gap-2 text-primary hover:text-primary/80 transition-colors"
            >
              <ArrowLeft className="w-5 h-5" />
              {isRTL ? "العودة للرئيسية" : "Back to Home"}
            </Link>
            <Button
              variant="ghost"
              size="icon"
              onClick={() => i18n.changeLanguage(i18n.language === "en" ? "ar" : "en")}
              className="rounded-full"
            >
              <Globe className="w-5 h-5" />
            </Button>
          </div>
        </div>
      </nav>

      <main className="flex items-center justify-center py-12 px-4">
        <div className="w-full max-w-md">
          <div className="bg-white rounded-2xl shadow-lg p-8">
            <div className="text-center mb-8">
              <h1 className="text-2xl font-bold text-gray-900 mb-2">
                {isLogin
                  ? isRTL ? "تسجيل الدخول" : "Login"
                  : isRTL ? "إنشاء حساب جديد" : "Create Account"}
              </h1>
              <p className="text-gray-600">
                {isLogin
                  ? isRTL ? "أدخل بياناتك للوصول للمساحات" : "Enter your credentials to access spaces"
                  : isRTL ? "سجل للدخول إلى المساحات" : "Register to access spaces"}
              </p>
            </div>

            <form onSubmit={handleSubmit} className="space-y-4">
              {!isLogin && (
                <>
                  <div className="space-y-2">
                    <Label htmlFor="fullName" className="flex items-center gap-2">
                      <User className="w-4 h-4" />
                      {isRTL ? "الاسم الكامل" : "Full Name"}
                    </Label>
                    <Input
                      id="fullName"
                      type="text"
                      value={formData.fullName}
                      onChange={(e) => setFormData({ ...formData, fullName: e.target.value })}
                      placeholder={isRTL ? "أدخل اسمك الكامل" : "Enter your full name"}
                      required={!isLogin}
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="phone" className="flex items-center gap-2">
                      <Phone className="w-4 h-4" />
                      {isRTL ? "رقم الهاتف (اختياري)" : "Phone (optional)"}
                    </Label>
                    <Input
                      id="phone"
                      type="tel"
                      value={formData.phone}
                      onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                      placeholder={isRTL ? "أدخل رقم هاتفك" : "Enter your phone number"}
                    />
                  </div>
                </>
              )}

              <div className="space-y-2">
                <Label htmlFor="email" className="flex items-center gap-2">
                  <Mail className="w-4 h-4" />
                  {isRTL ? "البريد الإلكتروني" : "Email"}
                </Label>
                <Input
                  id="email"
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  placeholder={isRTL ? "أدخل بريدك الإلكتروني" : "Enter your email"}
                  required
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="password" className="flex items-center gap-2">
                  <Lock className="w-4 h-4" />
                  {isRTL ? "كلمة المرور" : "Password"}
                </Label>
                <Input
                  id="password"
                  type="password"
                  value={formData.password}
                  onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                  placeholder={isRTL ? "أدخل كلمة المرور" : "Enter your password"}
                  required
                  minLength={6}
                />
              </div>

              <Button type="submit" className="w-full" disabled={loading}>
                {loading
                  ? isRTL ? "جاري التحميل..." : "Loading..."
                  : isLogin
                  ? isRTL ? "تسجيل الدخول" : "Login"
                  : isRTL ? "إنشاء الحساب" : "Create Account"}
              </Button>
            </form>

            <div className="mt-6 text-center">
              <button
                type="button"
                onClick={() => setIsLogin(!isLogin)}
                className="text-primary hover:underline"
              >
                {isLogin
                  ? isRTL ? "ليس لديك حساب؟ سجل الآن" : "Don't have an account? Sign up"
                  : isRTL ? "لديك حساب؟ سجل دخول" : "Already have an account? Login"}
              </button>
            </div>
          </div>
        </div>
      </main>
    </div>
  );
};

export default Auth;
